import React, { useEffect, useState } from "react";
import { Award, Save, Flame, Plus, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const ROLES = [
  { key: "customer", label: "Cliente" },
  { key: "rider", label: "Rider" },
  { key: "business", label: "Attività" },
  { key: "professionista", label: "Professionista" },
  { key: "organizer", label: "Organizzatore" },
];

const KINDS = [
  { key: "reward", label: "Reward" },
  { key: "access", label: "Accesso" },
  { key: "status", label: "Status" },
];

export default function RewardsCeoPanel() {
  const [cfg, setCfg] = useState(null);
  const [role, setRole] = useState("customer");
  const [saving, setSaving] = useState(false);
  const [openLevel, setOpenLevel] = useState(null);

  useEffect(() => { api.get("/ceo/growth/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return <div className="text-white/60 p-6">Caricamento…</div>;

  const levels = (cfg.levels && cfg.levels[role]) || [];
  const camp = cfg.xp_campaign || { active: false, multiplier: 2, label: "Weekend x2 XP" };

  const upd = (fn) => { const next = JSON.parse(JSON.stringify(cfg)); fn(next); setCfg(next); };
  const setField = (i, field, val) => upd((n) => { n.levels[role][i][field] = val; });
  const addPriv = (i) => upd((n) => { const l = n.levels[role][i]; if (!l.privileges) l.privileges = []; l.privileges.push({ label: "", kind: "reward" }); });
  const setPriv = (i, j, field, val) => upd((n) => { n.levels[role][i].privileges[j][field] = val; });
  const delPriv = (i, j) => upd((n) => { n.levels[role][i].privileges.splice(j, 1); });
  const setCamp = (patch) => setCfg({ ...cfg, xp_campaign: { ...camp, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/ceo/growth/config", { levels: cfg.levels, xp_campaign: cfg.xp_campaign });
      toast.success("Configurazione Rewards salvata");
    } catch (e) { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="ceo-rewards-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center gap-2"><Award className="h-5 w-5 text-[#00E676]" /> Rewards Control Center</h3>
        <button data-testid="rewards-ceo-save" onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#00E676] text-black font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Salvo…" : "Salva"}
        </button>
      </div>

      {/* Campagna XP */}
      <div className="bg-[#1C1C1F] border border-white/10 rounded-lg p-4" data-testid="rewards-ceo-campaign">
        <div className="flex items-center gap-2 font-medium mb-3"><Flame className="h-4 w-4 text-[#FF9F45]" /> Campagna XP temporanea</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="campaign-active" checked={!!camp.active} onChange={(e) => setCamp({ active: e.target.checked })} />
            Attiva
          </label>
          <div className="text-sm">Moltiplicatore
            <input type="number" min="1" step="1" data-testid="campaign-multiplier" value={camp.multiplier} onChange={(e) => setCamp({ multiplier: parseInt(e.target.value || 1, 10) })}
              className="ml-2 w-16 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1" />x
          </div>
          <input type="text" data-testid="campaign-label" value={camp.label || ""} onChange={(e) => setCamp({ label: e.target.value })}
            placeholder="Etichetta (es. Weekend x2 XP)" className="bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
        </div>
        <div className="mt-3">
          <div className="text-xs text-white/50 mb-2">Ruoli inclusi nella campagna (nessuna selezione = tutti i ruoli)</div>
          <div className="flex flex-wrap gap-2">
            {ROLES.filter((r) => ["customer", "rider", "business"].includes(r.key)).map((r) => {
              const sel = (camp.eligible_roles || []).includes(r.key);
              return (
                <button key={r.key} type="button" data-testid={`campaign-role-${r.key}`}
                  onClick={() => {
                    const cur = new Set(camp.eligible_roles || []);
                    sel ? cur.delete(r.key) : cur.add(r.key);
                    setCamp({ eligible_roles: Array.from(cur) });
                  }}
                  className={`px-3 py-1 rounded-full text-xs border ${sel ? "bg-[#FF9F45] text-black border-[#FF9F45]" : "border-white/15 text-white/70"}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <label className="text-xs text-white/60">Inizio (opzionale)
            <input type="datetime-local" data-testid="campaign-starts" value={(camp.starts_at || "").slice(0, 16)}
              onChange={(e) => setCamp({ starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
          </label>
          <label className="text-xs text-white/60">Fine (opzionale)
            <input type="datetime-local" data-testid="campaign-ends" value={(camp.ends_at || "").slice(0, 16)}
              onChange={(e) => setCamp({ ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
              className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
          </label>
        </div>
      </div>

      {/* Selettore ruolo */}
      <div className="flex flex-wrap gap-2">
        {ROLES.map((r) => (
          <button key={r.key} data-testid={`rewards-role-${r.key}`} onClick={() => { setRole(r.key); setOpenLevel(null); }}
            className={`px-3 py-1.5 rounded-full text-sm border ${role === r.key ? "bg-[#00E676] text-black border-[#00E676]" : "border-white/15 text-white/70"}`}>{r.label}</button>
        ))}
      </div>

      {/* Livelli configurabili: soglie + privilegi + reward */}
      <div className="space-y-3" data-testid="ceo-levels-editor">
        <div className="text-sm text-white/60">Configura livelli di <b>{ROLES.find((r) => r.key === role)?.label}</b>: nome, soglia XP, privilegi (Reward/Accesso/Status), reward e crediti. Tutto senza modificare il codice.</div>
        {levels.map((l, i) => {
          const isOpen = openLevel === i;
          return (
            <div key={l.key} className="bg-[#1C1C1F] border border-white/10 rounded-lg" data-testid={`ceo-level-${l.key}`}>
              <div className="flex items-center gap-3 p-3">
                <input type="text" data-testid={`level-name-${l.key}`} value={l.name} onChange={(e) => setField(i, "name", e.target.value)}
                  className="w-40 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 font-medium" />
                <div className="flex items-center gap-1 text-sm">
                  <input type="number" min="0" step="50" data-testid={`level-xp-${l.key}`} value={l.xp} onChange={(e) => setField(i, "xp", Math.max(0, parseInt(e.target.value || 0, 10)))}
                    className="w-24 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1" /><span className="text-white/40">XP</span>
                </div>
                <span className="text-xs text-white/40 flex-1 truncate">{(l.privileges || l.perks || []).length} privilegi</span>
                <button data-testid={`level-toggle-${l.key}`} onClick={() => setOpenLevel(isOpen ? null : i)} className="text-white/60 flex items-center gap-1 text-xs">
                  Dettagli <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
              </div>

              {isOpen && (
                <div className="px-3 pb-3 space-y-3 border-t border-white/8 pt-3">
                  <div className="grid sm:grid-cols-3 gap-2">
                    <label className="text-xs text-white/60">Icona/Badge
                      <input type="text" value={l.icon || ""} onChange={(e) => setField(i, "icon", e.target.value)} placeholder="es. crown"
                        className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="text-xs text-white/60">Crediti al raggiungimento
                      <input type="number" min="0" step="10" data-testid={`level-credits-${l.key}`} value={l.credits || 0} onChange={(e) => setField(i, "credits", Math.max(0, parseInt(e.target.value || 0, 10)))}
                        className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
                    </label>
                    <label className="flex items-end gap-2 text-xs text-white/60 pb-1">
                      <input type="checkbox" data-testid={`level-credits-active-${l.key}`} checked={!!l.credits_active} onChange={(e) => setField(i, "credits_active", e.target.checked)} />
                      Crediti attivi (costo economico)
                    </label>
                  </div>
                  <label className="text-xs text-white/60 block">Reward testuale (es. "-20% presso Partner X ad agosto")
                    <input type="text" data-testid={`level-reward-${l.key}`} value={l.reward || ""} onChange={(e) => setField(i, "reward", e.target.value)}
                      className="mt-1 w-full bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
                  </label>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs uppercase tracking-wide text-white/40">Privilegi</div>
                      <button data-testid={`level-add-priv-${l.key}`} onClick={() => addPriv(i)} className="flex items-center gap-1 text-xs text-[#00E676]"><Plus className="h-3.5 w-3.5" /> Aggiungi</button>
                    </div>
                    <div className="space-y-2">
                      {(l.privileges || []).map((p, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <input type="text" value={p.label} onChange={(e) => setPriv(i, j, "label", e.target.value)} placeholder="Descrizione privilegio"
                            className="flex-1 bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm" />
                          <select value={p.kind} onChange={(e) => setPriv(i, j, "kind", e.target.value)}
                            className="bg-[#0A0A0C] border border-white/10 rounded px-2 py-1 text-sm">
                            {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                          </select>
                          <button onClick={() => delPriv(i, j)} className="text-white/40 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      ))}
                      {(!l.privileges || l.privileges.length === 0) && <div className="text-white/40 text-xs">Nessun privilegio. Aggiungine uno.</div>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {levels.length === 0 && <div className="text-white/40 text-sm">Nessun livello configurato per questo ruolo.</div>}
      </div>

      <p className="text-xs text-white/40">Le soglie XP, i privilegi e le campagne x2 XP si controllano qui. I privilegi con costo economico (crediti) sono <b>disattivi di default</b> e vanno abilitati manualmente. Missioni e badge si gestiscono nel pannello “Missioni”.</p>
    </div>
  );
}
